import { Component } from 'react';
import PropTypes from 'prop-types';
import flow from 'lodash/flow';
import some from 'lodash/some';
import get from 'lodash/get';
import { connect } from 'react-redux';
import classNames from 'clsx';
import connectWithExperiments from '../../decorators/connectWithExperiments';
import withRecurlyConfig from '../../decorators/subscription/withRecurlyConfig';
import { PAYMENT_FORM_OR_ENTER_YOUR_CARD } from '../../constants/localizations/subscription';
import hasFreeTrial from '../utils/hasFreeTrial';
import {
  ENABLE_PAYPAL_PAYMENT_METHOD,
  PAYPAL_DISABLED_COUNTRY_LIST,
} from '../../constants/experiments/payment';
import { LocationAndLocalizationContext } from '../../providers/LocationAndLocalizationProvider';
import AuthFlowPromoOfferFinePrint from '../auth/shared/AuthFlowPromoOfferFinePrint';
import CreditCardForm from './CreditCardForm';
import PaypalPayment from './PaypalPayment';
import RadioButtonWrapper from './RadioButtonWrapper';
import AutoRenewCopy from './AutoRenewCopy';
import PaymentDisclaimer from './PaymentDisclaimer';
import css from './payment.module.scss';

// exported for testing
export class PaymentOptionsWrapper extends Component {
  static propTypes = {
    // from parent
    selectedProductSku: PropTypes.string,
    breakpoint: PropTypes.number.isRequired,
    products: PropTypes.array,
    isFormDisabled: PropTypes.bool.isRequired,
    disableCreditCardForm: PropTypes.func.isRequired,
    paymentFlowLayoutProps: PropTypes.object.isRequired,
    onSubmitSuccess: PropTypes.func.isRequired,
    onSubmitFailure: PropTypes.func.isRequired,
    openFailureDialog: PropTypes.func.isRequired,
    handlePaymentFlowChange: PropTypes.func.isRequired,
    selectedProduct: PropTypes.object, // not required for PaymentInformation
    setSelectedProductSku: PropTypes.func, // not required for PaymentInformation
    setRecurlyErrorState: PropTypes.func.isRequired, // used by ensureRecurly/withRecurlyTracking
    setRecurlyLoadingState: PropTypes.func, // used by ensureRecurlyJs
    currentPaymentFlow: PropTypes.string.isRequired,
    actions: PropTypes.object.isRequired,
    defaultSubmitButtonLabel: PropTypes.string.isRequired,
    formId: PropTypes.string.isRequired,
    onSubmit: PropTypes.func.isRequired,
    hideFooterCopy: PropTypes.bool,
    isPartnerFlow: PropTypes.bool,
    // from connectWithExperiments
    [ENABLE_PAYPAL_PAYMENT_METHOD]: PropTypes.bool,
    [PAYPAL_DISABLED_COUNTRY_LIST]: PropTypes.array,
    // from mapStateToProps
    userLocation: PropTypes.string,
    // from withRecurlyConfig
    recurly: PropTypes.object.isRequired,
    // from redux-form
    form: PropTypes.object.isRequired,
    error: PropTypes.object,
    submitting: PropTypes.bool,
    handleSubmit: PropTypes.func,
  };

  static contextType = LocationAndLocalizationContext;

  render() {
    const { getLocalizedText } = this.context;
    const {
      selectedProductSku,
      products,
      form,
      isFormDisabled,
      disableCreditCardForm,
      paymentFlowLayoutProps,
      actions,
      selectedProduct,
      setSelectedProductSku,
      hideFooterCopy,
      [ENABLE_PAYPAL_PAYMENT_METHOD]: isPaypalEnabled,
      [PAYPAL_DISABLED_COUNTRY_LIST]: paypalDisabledCountrylist,
      userLocation,
      isPartnerFlow = false,
    } = this.props;
    const { isPairingFlow } = paymentFlowLayoutProps || {};

    const isFreeTrialEnabled = !!hasFreeTrial(selectedProductSku, products);
    const hasSetupFee = !!selectedProduct?.setupPrice;

    const isPaypalCountryDisabled = some(
      paypalDisabledCountrylist,
      (country) => country === userLocation,
    );

    return (
      <div>
        {products && (
          <RadioButtonWrapper
            products={products}
            isPairingFlow={isPairingFlow}
            setSelectedProductSku={setSelectedProductSku}
            selectedProduct={selectedProduct}
          />
        )}
        {isPaypalEnabled && !isPaypalCountryDisabled && (
          <div>
            <div
              className={classNames(
                'row',
                css.paymentFieldSection,
                css.altPaymentOptionsRow,
              )}
            >
              <div className={css.altPaymentButtonWrapper}>
                <PaypalPayment
                  {...this.props}
                  logSubscribeActivity={actions.logSubscribeActivity}
                />
              </div>
            </div>
            <div className="row">
              <div className="col-xs-12">
                <div
                  className={classNames(css.creditCardFormTitle, {
                    [css.isPairingFlow]: isPairingFlow,
                  })}
                >
                  {getLocalizedText(PAYMENT_FORM_OR_ENTER_YOUR_CARD)}
                </div>
              </div>
            </div>
          </div>
        )}
        <CreditCardForm
          {...this.props}
          disableCreditCardForm={disableCreditCardForm}
          isFormDisabled={isFormDisabled}
          freeTrialEnabled={isFreeTrialEnabled}
          hasSetupFee={hasSetupFee}
          form={form}
        />
        {!hideFooterCopy && (
          <div
            className={classNames(css.paymentOptionsFooterContainer, {
              [css.isPairingFlow]: isPairingFlow,
            })}
          >
            <AutoRenewCopy isPairingFlow={isPairingFlow} />
            {isPairingFlow && (
              <AuthFlowPromoOfferFinePrint
                containerClassName={css.finePrintStyle}
              />
            )}
            <PaymentDisclaimer
              isPartnerOrPairingFlow={isPairingFlow || isPartnerFlow}
            />
          </div>
        )}
      </div>
    );
  }
}

export function mapStateToProps(state) {
  const location = get(state, 'config.location.country_code');

  return {
    userLocation: location,
  };
}

export default flow(
  withRecurlyConfig,
  connect(mapStateToProps),
  connectWithExperiments([
    ENABLE_PAYPAL_PAYMENT_METHOD,
    PAYPAL_DISABLED_COUNTRY_LIST,
  ]),
)(PaymentOptionsWrapper);
